/**
 * Custom text field limits and constants.
 *
 * These limits match the backend validation in:
 * - voicethera-backend/app/constants/custom_text.py
 * - voicethera-backend/app/models/schemas.py
 */

// Maximum character lengths for custom text fields
export const CUSTOM_TEXT_MAX_LENGTH = 20000; // ~20 minutes of TTS at ~130 words/min (~3000 words)
export const CUSTOM_TEXT_INSTRUCTIONS_MAX_LENGTH = 1000; // Instructions for blend_custom mode

// Minimum lengths
export const CUSTOM_TEXT_MIN_LENGTH = 10; // Minimum meaningful content
export const CUSTOM_TEXT_INSTRUCTIONS_MIN_LENGTH = 5; // Minimum instruction length

// Valid custom text modes
export const CUSTOM_TEXT_MODES = [
  'verbatim',       // Use custom text directly, skip AI generation
  'blend_beginning',  // AI starts with custom text, then continues
  'blend_end',        // AI generates, ends with custom text
  'blend_middle',     // AI incorporates text at midpoint
  'blend_woven',      // AI weaves custom themes throughout
  'blend_custom',     // AI follows custom_text_instructions
] as const;

export type CustomTextMode = typeof CUSTOM_TEXT_MODES[number];

// Validation error messages
export const VALIDATION_ERRORS = {
  custom_text_too_long: `Custom text must be at most ${CUSTOM_TEXT_MAX_LENGTH.toLocaleString()} characters`,
  custom_text_too_short: `Custom text must be at least ${CUSTOM_TEXT_MIN_LENGTH} characters`,
  instructions_too_long: `Instructions must be at most ${CUSTOM_TEXT_INSTRUCTIONS_MAX_LENGTH.toLocaleString()} characters`,
  instructions_too_short: `Instructions must be at least ${CUSTOM_TEXT_INSTRUCTIONS_MIN_LENGTH} characters`,
  instructions_required: 'Instructions are required when using blend_custom mode',
  custom_text_required: 'Custom text is required when custom text mode is enabled',
};

// Helper functions
export function validateCustomText(
  text: string,
  mode: CustomTextMode | null
): { isValid: boolean; error?: string } {
  if (mode && !text.trim()) {
    return { isValid: false, error: VALIDATION_ERRORS.custom_text_required };
  }

  if (text) {
    if (text.length > CUSTOM_TEXT_MAX_LENGTH) {
      return { isValid: false, error: VALIDATION_ERRORS.custom_text_too_long };
    }
    if (text.trim().length < CUSTOM_TEXT_MIN_LENGTH) {
      return { isValid: false, error: VALIDATION_ERRORS.custom_text_too_short };
    }
  }

  return { isValid: true };
}

export function validateCustomTextInstructions(
  instructions: string,
  mode: CustomTextMode | null
): { isValid: boolean; error?: string } {
  if (mode === 'blend_custom' && !instructions.trim()) {
    return { isValid: false, error: VALIDATION_ERRORS.instructions_required };
  }

  if (instructions) {
    if (instructions.length > CUSTOM_TEXT_INSTRUCTIONS_MAX_LENGTH) {
      return { isValid: false, error: VALIDATION_ERRORS.instructions_too_long };
    }
    if (instructions.trim().length < CUSTOM_TEXT_INSTRUCTIONS_MIN_LENGTH) {
      return { isValid: false, error: VALIDATION_ERRORS.instructions_too_short };
    }
  }

  return { isValid: true };
}

// Estimate word count from text
export function estimateWordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

// Estimate duration in minutes (assuming ~130 words per minute for TTS)
export function estimateDuration(text: string): number {
  const words = estimateWordCount(text);
  return Math.ceil(words / 130);
}
